import React from "react";
import { findIndex } from "lodash-es";
import { Button, Grid, MenuItem, Typography } from "@mui/material";
import { Field, Form, Formik, FormikProps } from "formik";
import { TextField } from "formik-mui";
import styled from "styled-components";
import { TFunction, useTranslation } from "react-i18next";

import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { APIError, Segment, TimefactorType } from "src/types";
import { adornments } from "src/config/adornments";
import { segment as decimalScale } from "src/config/decimal-scales";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { useSegmentSchema } from "src/schemas/segment.schema";
import { DecimalScaleTextField } from "src/components/form-components/DecimalScaleTextField";

export type SegmentFormData = Omit<Segment, "segmentId" | "lineString_frontendSrid">;

interface Props {
  title?: string;
  readOnly?: boolean;
  data: SegmentFormData;
  timefactorType: TimefactorType;
  vehicleTypes: string[];
  roadTypes: string[];
  onClose: () => void;
  onSubmit: (values: SegmentFormData) => any;
  onDelete?: () => void;
  isLoading: boolean;
  errors?: APIError[] | null;
  formRef?: React.MutableRefObject<FormikProps<SegmentFormData> | null>;
}

export function SegmentForm(props: Props) {
  const { t } = useTranslation();
  const schema = useSegmentSchema();

  const initialValues: SegmentFormData = {
    roadType: props.data.roadType,
    height_m: props.data.height_m,
    speed_km_h: props.data.speed_km_h,
    vehicleCounts: props.data.vehicleCounts,
  };

  return render();

  function render() {
    return (
      <Formik
        innerRef={props.formRef}
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={async (values: SegmentFormData) => {
          // console.log("🌀 ~ values", values);
          return props.onSubmit(values);
        }}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              {props.title && (
                <Grid item xs={12}>
                  <Typography variant="h2">{props.title}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body1">{t("label.trafficCount")}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("label.timefactorType")}: {t(`label.timefactorTypes.${props.timefactorType}`)}
                </Typography>
              </Grid>
              {props.vehicleTypes.map((vehicleType) => {
                const vehicleTypeIndex = findIndex(props.data.vehicleCounts, {
                  vehicleType,
                });
                return (
                  <Grid item xs={12} key={["vehicleCounts", vehicleType].join()}>
                    <Field
                      component={DecimalScaleTextField}
                      decimalScale={decimalScale.vehicleCounts}
                      name={`vehicleCounts[${vehicleTypeIndex}].count`}
                      label={t(`label.vehicleType.${vehicleType}`)}
                      type="number"
                      variant="outlined"
                      fullWidth
                      disabled={props.readOnly}
                      inputProps={{ step: "any" }}
                      helperText={oldVehicleCountsValueHelperText(props.data, vehicleTypeIndex, t)}
                    />
                  </Grid>
                );
              })}
              <Grid item xs={12}>
                <Typography>{t("label.segmentProperties")}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={DecimalScaleTextField}
                  decimalScale={decimalScale.speed_km_h}
                  name="speed_km_h"
                  label={t("label.speedLimit")}
                  type="number"
                  variant="outlined"
                  InputProps={adornments(t).kmPerHour}
                  fullWidth
                  disabled={props.readOnly}
                  inputProps={{ step: "any" }}
                  helperText={oldValueHelperText(props.data, "speedOld_km_h", t)}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={DecimalScaleTextField}
                  decimalScale={decimalScale.height_m}
                  name="height_m"
                  label={t("label.height")}
                  type="number"
                  variant="outlined"
                  InputProps={adornments(t).meter}
                  fullWidth
                  disabled={props.readOnly}
                  inputProps={{ step: "any" }}
                  helperText={oldValueHelperText(props.data, "heightOld_m", t)}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="roadType"
                  label={t("label.roadType")}
                  type="text"
                  select={true}
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                  helperText={oldValueHelperText(props.data, "roadTypeOld", t)}
                >
                  <MenuItem value={""}>
                    <em>{t("label.roadType_placeholder")}</em>
                  </MenuItem>
                  {props.roadTypes.map((roadType) => (
                    <MenuItem key={roadType} value={roadType}>
                      {t(`label.roadTypeDict.${roadType}`)}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
              {props.errors && (
                <Grid item xs={12}>
                  <ServerErrors data={props.errors} />
                </Grid>
              )}
              {!props.readOnly && (
                <>
                  <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                    <Button variant="text" onClick={props.onClose}>
                      {t("label.cancel")}
                    </Button>
                    <ButtonLoading
                      isLoading={isSubmitting || props.isLoading}
                      type="submit"
                      color="primary"
                      variant="contained"
                    >
                      {t("label.save")}
                    </ButtonLoading>
                  </ButtonGrid>

                  <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                    {props.onDelete && (
                      <DeleteButton variant="text" onClick={props.onDelete}>
                        {t("label.delete")}
                      </DeleteButton>
                    )}
                  </ButtonGrid>
                </>
              )}

              {props.readOnly && (
                <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                  <Button variant="text" onClick={props.onClose}>
                    {t("label.close")}
                  </Button>
                </ButtonGrid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }
}

const DeleteButton = styled(Button)`
  color: ${({ theme }) => theme.palette.error.main};
`;

/**
 * Utils
 */
function oldValueHelperText<T = SegmentFormData>(data: T, property: keyof T, t: TFunction) {
  if (!data[property]) return "";
  else if (property === "roadTypeOld") {
    return t("label.initialValue", { value: t(`label.roadTypeDict.${data[property]}`) });
  } else {
    return t("label.initialValue", { value: data[property] });
  }
}

function oldVehicleCountsValueHelperText(data: SegmentFormData, vehicleTypeIndex: number, t: TFunction) {
  if (!data.vehicleCountsOld) return "";
  else if (data.vehicleCountsOld.length === 0) return "";
  else {
    return t("label.initialValue", { value: data.vehicleCountsOld[vehicleTypeIndex].count });
  }
}
